import { useEffect, useState } from 'react';
import { Activity, BarChart3, Clock, MousePointerClick, Power, RefreshCw, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { api, UsageAnalyticsSummary, UsageAreaSummaryRow, UsagePageSummaryRow } from '../../services/api';

const PERIOD_OPTIONS = [7, 14, 30, 90];

function formatDuration(seconds?: number | null): string {
    const total = Math.max(0, Math.round(Number(seconds || 0)));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${total}s`;
}

function formatDate(value?: string | null): string {
    if (!value) return 'Never';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
}

function MetricCard({ title, value, hint, icon: Icon }: { title: string; value: string; hint: string; icon: any }) {
    return (
        <Card>
            <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</div>
                        <div className="mt-2 text-2xl font-bold text-slate-900">{value}</div>
                        <div className="mt-1 text-xs text-slate-500">{hint}</div>
                    </div>
                    <div className="rounded-xl bg-blue-50 p-2 text-blue-700">
                        <Icon className="h-5 w-5" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

function formatRatio(value?: number | null): string {
    const ratio = Math.max(0, Number(value || 0));
    return `${Math.round(ratio * 100)}%`;
}

function SignalBadges({ signals }: { signals?: string[] }) {
    if (!signals?.length) return <span className="text-xs text-slate-400">None</span>;
    return (
        <div className="flex flex-wrap justify-end gap-1">
            {signals.map((signal) => (
                <span key={signal} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                    {signal}
                </span>
            ))}
        </div>
    );
}

function AnalyticsCollectionPanel({
    enabled,
    saving,
    onToggle,
}: {
    enabled: boolean | null;
    saving: boolean;
    onToggle: () => void;
}) {
    const isEnabled = enabled !== false;
    return (
        <Card>
            <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <div className="text-sm font-semibold text-slate-900">Analytics Collection</div>
                    <p className="mt-1 text-xs text-slate-500">
                        Turning this off stops new tracking but keeps historical reports.
                    </p>
                </div>
                <Button
                    type="button"
                    variant={isEnabled ? 'default' : 'outline'}
                    disabled={saving || enabled === null}
                    onClick={onToggle}
                    aria-label={`Analytics collection ${isEnabled ? 'on' : 'off'}`}
                >
                    <Power className="mr-2 h-4 w-4" />
                    {saving ? 'Saving...' : isEnabled ? 'Collection On' : 'Collection Off'}
                </Button>
            </CardContent>
        </Card>
    );
}

function PageTable({ title, rows, mode }: { title: string; rows: UsagePageSummaryRow[]; mode: 'time' | 'visits' }) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg">{title}</CardTitle>
            </CardHeader>
            <CardContent>
                {rows.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
                        No usage data recorded for this period yet.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                                    <th className="py-2 pr-3">Page</th>
                                    <th className="py-2 pr-3 text-right">{mode === 'time' ? 'Active Time' : 'Visits'}</th>
                                    <th className="py-2 pr-3 text-right">Avg Active</th>
                                    <th className="py-2 pr-3 text-right">Dwell Time</th>
                                    <th className="py-2 pr-3 text-right">Users</th>
                                    <th className="py-2 text-right">Last Seen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={`${title}-${row.path}`} className="border-b last:border-0">
                                        <td className="py-3 pr-3">
                                            <div className="font-medium text-slate-900">{row.label || row.path}</div>
                                            <div className="text-xs text-slate-500">{row.path}</div>
                                        </td>
                                        <td className="py-3 pr-3 text-right font-semibold text-slate-800">
                                            {mode === 'time' ? formatDuration(row.active_duration_seconds) : row.visits.toLocaleString()}
                                        </td>
                                        <td className="py-3 pr-3 text-right text-slate-600">{formatDuration(row.active_average_duration_seconds)}</td>
                                        <td className="py-3 pr-3 text-right text-slate-600">{formatDuration(row.capped_duration_seconds ?? row.total_duration_seconds)}</td>
                                        <td className="py-3 pr-3 text-right text-slate-600">{row.active_users.toLocaleString()}</td>
                                        <td className="py-3 text-right text-xs text-slate-500">{formatDate(row.last_seen_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function ImprovementTargetsTable({ rows }: { rows: UsagePageSummaryRow[] }) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg">Improvement Targets</CardTitle>
            </CardHeader>
            <CardContent>
                {rows.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
                        No improvement targets identified for this period yet.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                                    <th className="py-2 pr-3">Page</th>
                                    <th className="py-2 pr-3 text-right">Active</th>
                                    <th className="py-2 pr-3 text-right">Median / P90</th>
                                    <th className="py-2 pr-3 text-right">Active/Dwell</th>
                                    <th className="py-2 text-right">Signal</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={`target-${row.path}`} className="border-b last:border-0">
                                        <td className="py-3 pr-3">
                                            <div className="font-medium text-slate-900">{row.label || row.path}</div>
                                            <div className="text-xs text-slate-500">{row.path}</div>
                                        </td>
                                        <td className="py-3 pr-3 text-right font-semibold text-slate-800">{formatDuration(row.active_duration_seconds)}</td>
                                        <td className="py-3 pr-3 text-right text-slate-600">
                                            {formatDuration(row.active_median_duration_seconds)} / {formatDuration(row.active_p90_duration_seconds)}
                                        </td>
                                        <td className="py-3 pr-3 text-right text-slate-600">{formatRatio(row.active_to_dwell_ratio)}</td>
                                        <td className="py-3 text-right"><SignalBadges signals={row.signals} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function WorkflowAreaTable({ rows }: { rows: UsageAreaSummaryRow[] }) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg">Top Workflow Areas</CardTitle>
            </CardHeader>
            <CardContent>
                {rows.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
                        No workflow-area analytics recorded for this period yet.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                                    <th className="py-2 pr-3">Workflow</th>
                                    <th className="py-2 pr-3 text-right">Visits</th>
                                    <th className="py-2 pr-3 text-right">Productive</th>
                                    <th className="py-2 pr-3 text-right">Active Time</th>
                                    <th className="py-2 pr-3 text-right">Dwell Time</th>
                                    <th className="py-2 text-right">Data Issues</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.canonical_path} className="border-b last:border-0">
                                        <td className="py-3 pr-3">
                                            <div className="font-medium text-slate-900">{row.label}</div>
                                            <div className="text-xs text-slate-500">{row.area} - {row.canonical_path}</div>
                                        </td>
                                        <td className="py-3 pr-3 text-right font-semibold text-slate-800">{row.visits.toLocaleString()}</td>
                                        <td className="py-3 pr-3 text-right text-slate-600">{row.productive_visits.toLocaleString()}</td>
                                        <td className="py-3 pr-3 text-right text-slate-600">{formatDuration(row.active_duration_seconds)}</td>
                                        <td className="py-3 pr-3 text-right text-slate-600">{formatDuration(row.capped_duration_seconds)}</td>
                                        <td className="py-3 text-right text-xs text-slate-500">
                                            {row.zero_duration_visits + row.superseded_visits === 0
                                                ? 'None'
                                                : `${row.zero_duration_visits} zero / ${row.superseded_visits} superseded`}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function DataQualityPanel({ summary }: { summary: UsageAnalyticsSummary | null }) {
    const quality = summary?.data_quality || {
        duration_cap_seconds: 7200,
        zero_duration_visits: 0,
        superseded_visits: 0,
        long_duration_visits: 0,
    };
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg">Data Quality</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid gap-3 md:grid-cols-3">
                    <div className="rounded-lg border border-slate-200 p-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Zero-duration visits</div>
                        <div className="mt-2 text-xl font-bold text-slate-900">{quality.zero_duration_visits.toLocaleString()}</div>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Superseded visits</div>
                        <div className="mt-2 text-xl font-bold text-slate-900">{quality.superseded_visits.toLocaleString()}</div>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Long visits capped</div>
                        <div className="mt-2 text-xl font-bold text-slate-900">{quality.long_duration_visits.toLocaleString()}</div>
                        <div className="mt-1 text-xs text-slate-500">Cap: {formatDuration(quality.duration_cap_seconds)}</div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export function UsageAnalyticsTab() {
    const [periodDays, setPeriodDays] = useState(7);
    const [summary, setSummary] = useState<UsageAnalyticsSummary | null>(null);
    const [collectionEnabled, setCollectionEnabled] = useState<boolean | null>(null);
    const [loading, setLoading] = useState(false);
    const [configSaving, setConfigSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadSummary = async () => {
        try {
            setLoading(true);
            setError(null);
            setSummary(await api.system.getUsageAnalyticsSummary(periodDays));
        } catch (err: any) {
            setError(err?.message || 'Failed to load usage analytics');
            setSummary(null);
        } finally {
            setLoading(false);
        }
    };

    const loadConfig = async () => {
        try {
            const config = await api.system.getUsageAnalyticsConfig();
            setCollectionEnabled(config.enabled);
        } catch (err: any) {
            setError(err?.message || 'Failed to load usage analytics config');
        }
    };

    const toggleCollection = async () => {
        const nextEnabled = !(collectionEnabled !== false);
        try {
            setConfigSaving(true);
            setError(null);
            const config = await api.system.updateUsageAnalyticsConfig({ enabled: nextEnabled });
            setCollectionEnabled(config.enabled);
        } catch (err: any) {
            setError(err?.message || 'Failed to update usage analytics config');
        } finally {
            setConfigSaving(false);
        }
    };

    useEffect(() => {
        loadConfig();
        loadSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodDays]);

    const totals = summary?.totals || {
        visits: 0,
        active_users: 0,
        total_duration_seconds: 0,
        average_duration_seconds: 0,
        active_duration_seconds: 0,
        active_average_duration_seconds: 0,
    };

    return (
        <div className="space-y-5">
            <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
                <div>
                    <h2 className="text-xl font-bold text-slate-900">Usage Analytics</h2>
                    <p className="mt-1 text-sm text-slate-500">
                        Internal page-time analytics for product improvement. No form values or third-party trackers are collected.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {PERIOD_OPTIONS.map((days) => (
                        <Button
                            key={days}
                            type="button"
                            size="sm"
                            variant={periodDays === days ? 'default' : 'outline'}
                            onClick={() => setPeriodDays(days)}
                        >
                            {days}d
                        </Button>
                    ))}
                    <Button type="button" size="sm" variant="outline" disabled={loading} onClick={loadSummary}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Refresh
                    </Button>
                </div>
            </div>

            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            <AnalyticsCollectionPanel
                enabled={collectionEnabled}
                saving={configSaving}
                onToggle={toggleCollection}
            />

            <div className="grid gap-4 md:grid-cols-4">
                <MetricCard title="Active Time" value={formatDuration(totals.active_duration_seconds)} hint="Time with recent user activity" icon={Activity} />
                <MetricCard title="Active Users" value={totals.active_users.toLocaleString()} hint="Users with tracked page activity" icon={Users} />
                <MetricCard title="Dwell Time" value={formatDuration(totals.capped_duration_seconds ?? totals.total_duration_seconds)} hint="Visible page time capped per visit" icon={Clock} />
                <MetricCard title="Visits" value={totals.visits.toLocaleString()} hint={`${periodDays}-day page visits`} icon={MousePointerClick} />
                <MetricCard title="Productive" value={(totals.productive_visits ?? totals.visits).toLocaleString()} hint="Visits with usable tracked time" icon={BarChart3} />
            </div>

            {loading && !summary ? (
                <Card>
                    <CardContent className="p-8 text-center text-sm text-slate-500">Loading usage analytics...</CardContent>
                </Card>
            ) : (
                <>
                    <ImprovementTargetsTable rows={summary?.improvement_targets || []} />
                    <div className="grid gap-5 xl:grid-cols-2">
                        <WorkflowAreaTable rows={summary?.top_areas || []} />
                        <DataQualityPanel summary={summary} />
                    </div>
                    <div className="grid gap-5 xl:grid-cols-2">
                        <PageTable title="Top Pages by Time" rows={summary?.top_pages_by_time || []} mode="time" />
                        <PageTable title="Top Pages by Visits" rows={summary?.top_pages_by_visits || []} mode="visits" />
                    </div>
                </>
            )}

            {summary?.top_users?.length ? (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Active Users</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                                        <th className="py-2 pr-3">User</th>
                                        <th className="py-2 pr-3 text-right">Visits</th>
                                        <th className="py-2 pr-3 text-right">Active Time</th>
                                        <th className="py-2 pr-3 text-right">Dwell Time</th>
                                        <th className="py-2 text-right">Avg Active</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summary.top_users.map((user) => (
                                        <tr key={user.user_id} className="border-b last:border-0">
                                            <td className="py-3 pr-3">
                                                <div className="font-medium text-slate-900">{user.full_name || user.username || `User ${user.user_id}`}</div>
                                                <div className="text-xs text-slate-500">{user.role || 'User'}</div>
                                            </td>
                                            <td className="py-3 pr-3 text-right text-slate-600">{user.visits.toLocaleString()}</td>
                                            <td className="py-3 pr-3 text-right font-semibold text-slate-800">{formatDuration(user.active_duration_seconds)}</td>
                                            <td className="py-3 pr-3 text-right text-slate-600">{formatDuration(user.total_duration_seconds)}</td>
                                            <td className="py-3 text-right text-slate-600">{formatDuration(user.active_average_duration_seconds)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            ) : null}
        </div>
    );
}
